import { addOfferSelection, offerSelection } from "./iki-ritual-offers.js";
import { safeCompanionRoot } from "./iki-companion-selection.min.js";

export function ritualSelection(rows) {
  if (!rows.some((row) => row.core)) throw new Error("Choose a core setup.");
  const products = new Set();
  const selection = offerSelection(
    rows.map((row) => {
      const checked = row.core || row.checked;
      if (checked && products.has(row.productId))
        throw new Error("A piece is included twice.");
      if (checked) products.add(row.productId);
      return { ...row, checked };
    }),
  );
  return selection;
}

export async function addRitualSetup(
  fetcher,
  base,
  origin,
  selection,
  currency,
  signal,
) {
  const cart = new URL("cart.js", base).href;
  const ids = new Set(selection.selected.map((piece) => piece.productId));
  let checkedBag = false;
  const guarded = async (input, options) => {
    const response = await fetcher(input, options);
    if (String(input) === cart && !checkedBag && response.ok) {
      const body = await response.clone().json();
      if (body.currency !== currency) throw new Error("currency_changed");
      if (body.items?.some((item) => ids.has(String(item.product_id))))
        throw new Error("already_in_bag");
      checkedBag = true;
    }
    return response;
  };
  return addOfferSelection(guarded, base, origin, selection, signal);
}

const mounted = new WeakSet();
function mount(root) {
  if (mounted.has(root)) return;
  const controls = root.querySelector("[data-builder-controls]");
  const question = root.querySelector("[data-builder-question]");
  const answers = root.querySelector("[data-builder-answers]");
  const next = root.querySelector("[data-builder-continue]");
  const summary = root.querySelector("[data-builder-summary]");
  const total = root.querySelector("[data-builder-total]");
  const add = root.querySelector("[data-builder-add]");
  const status = root.querySelector("[data-builder-status]");
  const recipes = [...root.querySelectorAll("[data-builder-recipe]")];
  if (
    !controls ||
    !question ||
    !answers ||
    !next ||
    !summary ||
    !total ||
    !add ||
    !status ||
    !recipes.length
  )
    return;
  let base, money;
  try {
    base = safeCompanionRoot(root.dataset.cartRoot, window.location.origin);
    money = new Intl.NumberFormat(root.dataset.locale || "en-GB", {
      style: "currency",
      currency: root.dataset.currency,
    });
  } catch {
    return;
  }
  mounted.add(root);
  let chosen = null,
    busy = false,
    locked = false;
  const controller = new AbortController();
  const clear = () => {
    chosen = null;
    summary.hidden = true;
    status.textContent = "";
    recipes.forEach((recipe) => {
      recipe.hidden = true;
      recipe.querySelectorAll("[data-builder-extra]").forEach((input) => {
        input.checked = false;
      });
      recipe.querySelectorAll("[data-builder-addition]").forEach((piece) => {
        piece.hidden = true;
      });
    });
  };
  const read = () =>
    ritualSelection(
      [...chosen.querySelectorAll("[data-builder-piece]")].map((piece) => ({
        core: piece.dataset.core === "true",
        checked: piece.querySelector("[data-builder-extra]")?.checked === true,
        productId: piece.dataset.productId,
        url: piece.dataset.productUrl,
        variantId: piece.dataset.variantId,
        price: Number(piece.dataset.price),
        disabled: piece.dataset.available !== "true",
        min: Number(piece.dataset.min),
        max: piece.dataset.max ? Number(piece.dataset.max) : null,
        increment: Number(piece.dataset.increment),
      })),
    );
  const sync = () => {
    add.disabled = true;
    if (!chosen) return;
    try {
      const selection = read();
      total.textContent = `Total ${money.format(selection.total / 100)}`;
      add.disabled = busy || locked || chosen.dataset.ready !== "true";
    } catch {
      total.textContent = "This setup is currently unavailable.";
    }
    add.setAttribute("aria-busy", String(busy));
  };
  const freeze = () => {
    root.querySelectorAll("input, [data-builder-continue]").forEach((input) => {
      input.disabled = true;
    });
  };
  root.addEventListener("change", (event) => {
    if (busy || locked) return;
    if (event.target.matches("[data-builder-ritual]")) {
      clear();
      answers.replaceChildren();
      const ritual = event.target.value;
      root.querySelector("[data-builder-question-title]").textContent = {
        matcha: "New to matcha?",
        coffee: "Coffee for one or two?",
        baking: "Starting your first loaf?",
      }[ritual];
      const matching = recipes.filter(
        (recipe) => recipe.dataset.ritual === ritual,
      );
      if (!matching.length) {
        const note = document.createElement("p");
        note.textContent =
          "There is no complete setup here at the moment. Explore the collection for individual pieces.";
        const link = document.createElement("a");
        link.href = `${base}collections/${ritual}`;
        link.textContent = `Explore ${ritual}`;
        answers.append(note, link);
      }
      matching.forEach((recipe) => {
        const label = document.createElement("label");
        label.className = "ritual-builder__answer";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = `setup-${root.id || root.querySelector("h1").id}`;
        radio.value = recipe.dataset.recipeId;
        radio.dataset.builderAnswer = "";
        const copy = document.createElement("span");
        copy.textContent = `${recipe.dataset.answer} · ${money.format(Number(recipe.dataset.coreTotal) / 100)}${recipe.dataset.ready !== "true" ? " · Currently unavailable" : ""}`;
        label.append(radio, copy);
        answers.append(label);
      });
      question.hidden = false;
      next.hidden = false;
      next.disabled = true;
    } else if (event.target.matches("[data-builder-answer]")) {
      clear();
      next.disabled = false;
    } else if (event.target.matches("[data-builder-extra]")) {
      sync();
    }
  });
  next.addEventListener("click", () => {
    if (busy || locked) return;
    const answer = answers.querySelector("input:checked");
    chosen = recipes.find(
      (recipe) => recipe.dataset.recipeId === answer?.value,
    );
    if (!chosen) return;
    chosen.hidden = false;
    chosen.querySelectorAll("[data-builder-addition]").forEach((piece) => {
      piece.hidden = false;
    });
    summary.hidden = false;
    sync();
    chosen.querySelector("header").focus();
  });
  add.addEventListener("click", async () => {
    if (busy || locked || !chosen || chosen.dataset.ready !== "true") return;
    let selection;
    try {
      selection = read();
    } catch {
      sync();
      return;
    }
    busy = true;
    freeze();
    status.textContent = "Adding your setup...";
    sync();
    const result = await addRitualSetup(
      window.fetch.bind(window),
      base,
      window.location.origin,
      selection,
      root.dataset.currency,
      controller.signal,
    );
    busy = false;
    locked = true;
    sync();
    status.textContent =
      result.status === "confirmed"
        ? "Your setup is in your bag."
        : result.attempted
          ? "Please check your bag before adding again."
          : "No pieces were added. Check your bag for existing pieces, or refresh for current prices and availability.";
    status.focus();
    if (result.status === "confirmed") window.location.assign(`${base}cart`);
  });
  window.addEventListener("pagehide", () => {
    locked = true;
    controller.abort();
    freeze();
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      locked = true;
      freeze();
      status.textContent = "Please refresh before adding a setup.";
      sync();
    }
  });
  clear();
  controls.hidden = false;
  root.querySelector("[data-builder-fallback]").hidden = true;
}
if (typeof document !== "undefined") {
  document.querySelectorAll("[data-ritual-builder]").forEach(mount);
  document.addEventListener("shopify:section:load", (event) =>
    event.target.querySelectorAll("[data-ritual-builder]").forEach(mount),
  );
}
